import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { TableColumn } from 'lp-components'
import { isEqual } from 'lodash'
import {
  PaginatedAndSortableTable,
  TableHeader,
  Tooltip,
  StatusBadge,
  Filter,
  SearchInput,
  TableHeading,
  DownloadTableButton,
} from 'components'
import {
  PlanAgreementsFilterDialog,
  PlanAgreementDetailsLinkBD,
} from '../components'
import {
  formatDate,
  displayEin,
  dateSorter,
  einSorter,
  formatUrl,
  tippyPlugins,
  displayAttribute,
  useSearchParams,
  convertToSearchParams,
} from 'utils'
import { Link, useHistory } from 'react-router-dom'
import {
  Path,
  AroActionItems,
  AccountAudience,
  isGfBDAgreementsEnabled,
} from 'config/portal'
import {
  ServicesAgreementTooltip,
  ServicesAgreementStatus,
  GrandfatheredUserValue,
} from 'config/automatic-rollovers'
import { BDCombinedAgreementStatus } from 'config/benefit-distributions'
import {
  INITIAL_INVESTMENT_TOOLTIP_CONTENT,
  BD_ADDENDUM_STATUS_TOOLTIP_CONTENT,
  ACCOUNT_NUMBER_TOOLTIP_CONTENT,
} from 'config/notifications'
import { Icon as InfoIcon } from 'images/info.svg'
import { Icon as UploadIcon } from 'images/upload-icon.svg'
import {
  PlanAgreementsTableHeaders,
  ProductAgreementType,
} from 'config/products'
import AgreementStatusCell from './AgreementStatusCell'

const { hideOnEsc, hideOnPopperBlur, preventEventBubbling } = tippyPlugins

const INITIAL_FILTERS = {
  agreementStatus: [],
  initialInvestment: [],
  planSponsor: [],
  startLastUpdatedDate: '',
  endLastUpdatedDate: '',
}
const propTypes = {
  servicesAgreements: PropTypes.arrayOf(Types.servicesAgreementSummary)
    .isRequired,
  bdPlanAgreements: PropTypes.arrayOf(Types.bdPlanAgreementsSummary).isRequired,
  noRowsFoundMessage: PropTypes.node,
  handleUploadClick: PropTypes.func.isRequired,
  tableId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    agreementStatus: PropTypes.arrayOf(PropTypes.string),
    initialInvestment: PropTypes.arrayOf(PropTypes.string),
    planSponsor: PropTypes.arrayOf(PropTypes.string),
    startLastUpdatedDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    endLastUpdatedDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  currentUser: Types.user.isRequired,
  agreementsType: PropTypes.string.isRequired,
  fieldMappings: PropTypes.arrayOf(PropTypes.object).isRequired,
  noServicesAgreements: PropTypes.bool.isRequired,
}

const defaultProps = {
  noRowsFoundMessage: null,
  filters: null,
}

function PlanAgreementDetailsLinkARO({ data: { serviceAgreementID, name } }) {
  return (
    <td>
      <Link
        to={(location) => {
          return {
            pathname: formatUrl(
              `${Path.SERVICES_AGREEMENTS}/${serviceAgreementID}`
            ),
            state: { search: location.search },
          }
        }}
        className="link-text"
        id="plan-name-link"
      >
        {name}
      </Link>
    </td>
  )
}

function getBDStatusLabel(agreementsType) {
  return agreementsType === ProductAgreementType.BD && isGfBDAgreementsEnabled()
    ? 'Agreement status'
    : 'Addendum status'
}

function PlanAgreementsTable({
  servicesAgreements,
  bdPlanAgreements,
  noRowsFoundMessage,
  handleUploadClick,
  tableId,
  filters,
  currentUser,
  agreementsType,
  fieldMappings,
  noServicesAgreements,
}) {
  const history = useHistory()
  const searchParams = useSearchParams()
  const [showFilterModal, setShowFilterModal] = useState(false)
  const hasFiltersApplied = !isEqual(filters, INITIAL_FILTERS)
  const isGrandfatheredUser =
    currentUser?.grandfatheredUser === GrandfatheredUserValue &&
    isGfBDAgreementsEnabled()

  const searchValue = searchParams.q ?? ''

  const updateSearchParams = (filters, searchValue) => {
    history.push({
      pathname: location.pathname,
      search: convertToSearchParams({
        ...filters,
        q: searchValue,
      }),
    })
  }
  const isBenefitDistributionsTable =
    agreementsType === ProductAgreementType.BD ||
    agreementsType === ProductAgreementType.NOTIFICATIONS
  const disabled = !isBenefitDistributionsTable
    ? !servicesAgreements?.length
    : !bdPlanAgreements?.length
  const showIcon = disabled
  const bdAgreementStatusLabel = getBDStatusLabel(agreementsType)
  const bdPlanAgreementsForExport = bdPlanAgreements?.map((agreement) => ({
    ...agreement,
    exportStatus: agreement.addendumStatus ?? agreement.agreementStatus,
  }))

  return (
    <>
      <TableHeading
        title={
          agreementsType === ProductAgreementType.ARO
            ? PlanAgreementsTableHeaders.ARO
            : PlanAgreementsTableHeaders.BD
        }
      >
        {!noServicesAgreements &&
          (agreementsType === ProductAgreementType.BD ||
            agreementsType === ProductAgreementType.NOTIFICATIONS) && (
            <DownloadTableButton
              data={bdPlanAgreementsForExport}
              fieldMappings={fieldMappings}
              fileName="Inspira Financial_Retirement Services_Benefit Distributions Services Agreements"
              disabled={disabled}
              showIcon={showIcon}
            />
          )}
        {!noServicesAgreements &&
          agreementsType === ProductAgreementType.ARO && (
            <DownloadTableButton
              data={servicesAgreements}
              fieldMappings={fieldMappings}
              fileName="Inspira Financial_Retirement Services_ARO Services Agreements"
              disabled={disabled}
              showIcon={showIcon}
            />
          )}
        <div className="input-container" id="services-agreements">
          <SearchInput
            type="search"
            aria-label="Search services agreement"
            placeholder={
              currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY
                ? 'Search plan name'
                : 'Search plan sponsor, plan name, or EIN'
            }
            onChange={(e) => updateSearchParams(filters, e.target.value)}
            value={searchValue}
          />
          <Filter
            toggleMenu={() => setShowFilterModal((s) => !s)}
            isActive={hasFiltersApplied}
          />
          {showFilterModal && (
            <PlanAgreementsFilterDialog
              filters={filters}
              handleSubmit={(filterValues) =>
                updateSearchParams(filterValues, searchValue)
              }
              clearAllFilters={() =>
                updateSearchParams(INITIAL_FILTERS, searchValue)
              }
              handleClose={() => setShowFilterModal(false)}
              servicesAgreements={servicesAgreements}
              bdPlanAgreements={bdPlanAgreements}
              agreementsType={agreementsType}
            />
          )}
        </div>
      </TableHeading>
      <PaginatedAndSortableTable
        data={
          isBenefitDistributionsTable ? bdPlanAgreements : servicesAgreements
        }
        initialAscending={false}
        initialColumn={
          isBenefitDistributionsTable ? 'dateCreated' : 'createdDate'
        }
        parentScrollContainerSelector="#main-content"
        className="services-agreements-table"
        noRowsFoundMessage={noRowsFoundMessage}
        tableId={tableId}
      >
        {currentUser.accountAudience !== AccountAudience.PLAN_FIDUCIARY && (
          <TableColumn
            label="Plan sponsor name"
            name={
              isBenefitDistributionsTable
                ? 'planSponsorName'
                : 'planSponsorCompany.name'
            }
            headerComponent={TableHeader}
            format={displayAttribute}
          />
        )}
        {currentUser.accountAudience !== AccountAudience.PLAN_FIDUCIARY && (
          <TableColumn
            label="EIN"
            name={isBenefitDistributionsTable ? 'ein' : 'planEIN'}
            format={displayEin}
            sortFunc={einSorter}
            headerComponent={TableHeader}
          />
        )}
        <TableColumn
          label="Complete legal plan name"
          name={isBenefitDistributionsTable ? 'completeLegalPlanName' : 'name'}
          component={
            isBenefitDistributionsTable
              ? ({ data }) => <PlanAgreementDetailsLinkBD data={data} />
              : PlanAgreementDetailsLinkARO
          }
          headerComponent={TableHeader}
        />

        {isBenefitDistributionsTable ? null : (
          <TableColumn
            label="Initial investment"
            name="portalAgreementType"
            headerComponent={(props) => (
              <TableHeader {...props}>
                <Tooltip
                  content={INITIAL_INVESTMENT_TOOLTIP_CONTENT}
                  ariaLabel="Initial investment information"
                  plugins={[hideOnEsc, hideOnPopperBlur, preventEventBubbling]}
                >
                  <InfoIcon className="icon-info" />
                </Tooltip>
              </TableHeader>
            )}
            format={displayAttribute}
          />
        )}
        {agreementsType === ProductAgreementType.ARO && (
          <TableColumn
            label="Agreement status"
            name="portalAgreementStatus"
            headerComponent={TableHeader}
            format={displayAttribute}
            component={(props) => (
              <AgreementStatusCell
                {...props}
                handleUploadClick={handleUploadClick}
              />
            )}
          />
        )}
        {isBenefitDistributionsTable && (
          <TableColumn
            label="Account number"
            name="accountNumber"
            headerComponent={(props) => (
              <TableHeader {...props}>
                <Tooltip
                  content={ACCOUNT_NUMBER_TOOLTIP_CONTENT}
                  ariaLabel="Account number information"
                  plugins={[hideOnEsc, hideOnPopperBlur, preventEventBubbling]}
                >
                  <InfoIcon className="icon-info" />
                </Tooltip>
              </TableHeader>
            )}
          />
        )}
        {isBenefitDistributionsTable && (
          <TableColumn
            label={bdAgreementStatusLabel}
            name="addendumStatus"
            headerComponent={(props) => (
              <TableHeader {...props}>
                {!isGfBDAgreementsEnabled() && (
                  <Tooltip
                    content={BD_ADDENDUM_STATUS_TOOLTIP_CONTENT}
                    ariaLabel="Addendum status information"
                    plugins={[
                      hideOnEsc,
                      hideOnPopperBlur,
                      preventEventBubbling,
                    ]}
                  >
                    <InfoIcon className="icon-info" />
                  </Tooltip>
                )}
              </TableHeader>
            )}
            format={displayAttribute}
            component={({ data, value }) => {
              const { name, addendumStatus, agreementStatus } = data
              const tooltipContent =
                ServicesAgreementTooltip[addendumStatus ?? agreementStatus]
              if (!addendumStatus && !agreementStatus)
                return (
                  <td className="status">
                    <span>{value}</span>
                  </td>
                )
              return (
                <td className="status">
                  <Tooltip
                    offset={[0, 18]}
                    content={tooltipContent}
                    ariaLabel={tooltipContent}
                  >
                    <StatusBadge status={addendumStatus ?? agreementStatus} />
                  </Tooltip>
                  {(addendumStatus ===
                    ServicesAgreementStatus.PENDING_SIGNATURES ||
                    addendumStatus ===
                      BDCombinedAgreementStatus.COMBINED_PENDING_SIGNATURES ||
                    (isGrandfatheredUser &&
                      agreementStatus ===
                        ServicesAgreementStatus.PENDING_SIGNATURES) ||
                    agreementStatus ===
                      BDCombinedAgreementStatus.COMBINED_PENDING_SIGNATURES) && (
                    <button
                      type="button"
                      className="button-text"
                      onClick={() => handleUploadClick(data)}
                      aria-label={`${AroActionItems.UPLOAD_SIGNED_AGREEMENT} for ${name}`}
                    >
                      {AroActionItems.UPLOAD_SIGNED_AGREEMENT}
                      <UploadIcon aria-hidden="true" className="upload-icon" />
                    </button>
                  )}
                </td>
              )
            }}
          />
        )}
        <TableColumn
          label="Date created"
          name={isBenefitDistributionsTable ? 'dateCreated' : 'createdDate'}
          format={(val) => formatDate(val)}
          sortFunc={dateSorter}
          headerComponent={TableHeader}
        />
      </PaginatedAndSortableTable>
    </>
  )
}

PlanAgreementsTable.propTypes = propTypes
PlanAgreementsTable.defaultProps = defaultProps
export default PlanAgreementsTable
